import { Loader2 } from 'lucide-react';
import React, { useCallback, useMemo, useRef } from 'react';

import { CaseTimelineEntry } from '@/components/CaseTimelineEntry';
import { CollapsibleSidebar } from '@/components/CollapsibleSidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { useCaseTimeline } from '@/hooks/useCaseTimeline';

// Event types hidden from the UI timeline. `case.task.created` is too noisy
// (one per workflow task) and provides little user-facing value;
// `case.backfilled` and `case.sla_breach.changed` are for analytics only.
const HIDDEN_EVENT_TYPES = new Set([
  'case.task.created',
  'case.backfilled',
  'case.sla_breach.changed',
]);

interface TimelineColumnProps {
  caseRecordId: string;
  isCollapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

const TimelineLoadingSkeleton: React.FC = () => (
  <div className="relative">
    <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
    <div className="space-y-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="relative flex items-start gap-3">
          <Skeleton className="size-6 rounded-full shrink-0 relative z-10" />
          <div className="flex-1 pt-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24 mt-1.5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/**
 * Activity timeline column. Owns the timeline data fetch and entry rendering;
 * chrome (collapse strip, header, expand/collapse) comes from CollapsibleSidebar.
 */
export const TimelineColumn: React.FC<TimelineColumnProps> = ({
  caseRecordId,
  isCollapsed,
  onCollapsedChange,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const {
    entries: allEntries,
    isLoading,
    isLoadingUsers,
    usersMap,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCaseTimeline({
    caseRecordId,
    enabled: !isCollapsed,
  });

  // The backend emits `case.human_audit.in_progress` (needed → in-progress)
  // natively (BI-1246), so no client-side synthesis is needed here.
  const entries = useMemo(
    () => allEntries.filter(entry => !entry.type || !HIDDEN_EVENT_TYPES.has(entry.type)),
    [allEntries],
  );

  const loadMoreFnRef = useRef(() => {});
  loadMoreFnRef.current = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      observed => {
        if (observed[0].isIntersecting) {
          loadMoreFnRef.current();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    );
    observerRef.current.observe(node);
  }, []);

  return (
    <CollapsibleSidebar
      label="Timeline"
      isCollapsed={isCollapsed}
      onCollapsedChange={onCollapsedChange}
    >
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
        {(isLoading || isLoadingUsers) && entries.length === 0 ? (
          <TimelineLoadingSkeleton />
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            <p className="text-sm">Failed to load activity</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No activity yet</p>
            <p className="text-xs mt-1">Events for this case will appear here</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-6">
              {entries.map(entry => (
                <CaseTimelineEntry
                  key={entry.id}
                  entry={entry}
                  user={entry.actor_id ? usersMap.get(entry.actor_id) : undefined}
                  usersMap={usersMap}
                />
              ))}
            </div>

            <div ref={sentinelRef} className="h-4">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground ml-2">Loading more...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSidebar>
  );
};
